import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography, useTheme } from '@mui/joy';

import CrewBatteryLine from '../../../assets/images/CrewBatteryLine.svg?react';

interface BatteryProps {
  value: number;
  time_to_land_s?: number;
  time_to_home_s?: number;
  time_remaining_s?: number;
  droneStatus?: string;
}

const Battery: React.FC<BatteryProps> = ({
  value,
  time_to_land_s = 0,
  time_to_home_s = 0,
  time_remaining_s = 0,
  droneStatus = ''
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [redDegrees, setRedDegrees] = useState(0);
  const [yellowDegrees, setYellowDegrees] = useState(0);
  const [greenDegrees, setGreenDegrees] = useState(0);

  // Simulation state
  const isSimulating = false;
  const [simTimeRemaining, setSimTimeRemaining] = useState(1800); // Start with 30 minutes (1800s)
  const [simTimeToHome, setSimTimeToHome] = useState(1); // Start with 5 minutes to home
  const simTimeToLand = 190; // Fixed at 20 seconds
  const [simValue, setSimValue] = useState(1.0); // Start with 100% battery

  const padding = 8;
  const markerWidth = 2;
  const markerHeight = 8;

  const MAX_DEGREES = 300;

  useEffect(() => {
    if (!isSimulating) return;

    const interval = setInterval(() => {
      setSimTimeRemaining((prev) => {
        const newTimeRemaining = Math.max(0, prev - 5); // Decrease by 5 seconds each tick

        const batteryPercentage = newTimeRemaining / 1800;
        setSimValue(batteryPercentage);

        return newTimeRemaining;
      });

      setSimTimeToHome((prev) => {
        return Math.min(prev + 60, 900); // Increase by 1 minute (60s), max 15 minutes (900s)
      });
    }, 50);

    return () => clearInterval(interval);
  }, [isSimulating]);

  const currentValue = isSimulating ? simValue : value;
  const currentTimeRemaining = isSimulating ? simTimeRemaining : time_remaining_s;
  const currentTimeToHome = isSimulating ? simTimeToHome : time_to_home_s;
  const currentTimeToLand = isSimulating ? simTimeToLand : time_to_land_s;

  const offSetSeconds = 60 * 2;
  const timeToHomeWarnSec = currentTimeToHome + offSetSeconds;
  const totalCapacitySeconds = currentTimeRemaining / currentValue;
  const timeToHomeWarn = timeToHomeWarnSec / totalCapacitySeconds;

  useEffect(() => {
    if (currentTimeRemaining <= 0 || currentValue <= 0) {
      setRedDegrees(0);
      setYellowDegrees(0);
      setGreenDegrees(0);
      return;
    }

    const redPercentageOfTotal = currentTimeToLand / totalCapacitySeconds;
    const yellowPercentageOfTotal = currentTimeToHome / totalCapacitySeconds;
    const greenPercentageOfTotal = currentTimeRemaining / totalCapacitySeconds; // This equals 'currentValue'

    const redDeg = Math.min(redPercentageOfTotal * MAX_DEGREES, MAX_DEGREES);
    const yellowDeg = Math.min(yellowPercentageOfTotal * MAX_DEGREES, MAX_DEGREES);
    const greenDeg = Math.min(greenPercentageOfTotal * MAX_DEGREES, MAX_DEGREES);

    if (currentTimeRemaining <= currentTimeToLand) {
      setRedDegrees(greenDeg);
      setYellowDegrees(0);
      setGreenDegrees(0);
    } else if (currentTimeRemaining <= currentTimeToHome) {
      setRedDegrees(redDeg);
      setYellowDegrees(greenDeg); // Actual remaining shown as yellow
      setGreenDegrees(0);
    } else {
      setRedDegrees(redDeg);
      setYellowDegrees(yellowDeg);
      setGreenDegrees(greenDeg);
    }
  }, [currentTimeToLand, currentTimeToHome, currentTimeRemaining, currentValue]);

  const remainingTimeDisplay = useMemo((): string => {
    const toMin = (seconds: number): string => {
      if (Number.isNaN(seconds) || !Number.isFinite(seconds) || seconds < 0) {
        return '--';
      }
      return (seconds / 60).toFixed(0);
    };

    if (
      Number.isNaN(currentTimeRemaining) ||
      !Number.isFinite(currentTimeRemaining) ||
      currentTimeRemaining <= 0 ||
      currentValue <= 0 ||
      Number.isNaN(currentValue) ||
      !Number.isFinite(currentValue)
    ) {
      return '--';
    }

    if (droneStatus === 'OnGround') {
      return toMin(currentTimeRemaining);
    }

    if (Number.isNaN(totalCapacitySeconds) || !Number.isFinite(totalCapacitySeconds) || totalCapacitySeconds <= 0) {
      return '--';
    }

    // That's the default offset for the warning from pilot app

    if (currentValue > timeToHomeWarn) {
      return toMin(currentTimeRemaining - timeToHomeWarnSec);
    }

    const timeToHome =
      currentTimeToHome && Number.isFinite(currentTimeToHome) && currentTimeToHome > 0
        ? currentTimeToHome / totalCapacitySeconds
        : 0;
    const batEmergThr = 0.05;

    if (currentValue > timeToHome && currentTimeToHome > 0) {
      return toMin(currentTimeRemaining - currentTimeToHome);
    }

    if (currentValue > batEmergThr && currentTimeToLand > 0) {
      return toMin(currentTimeRemaining - currentTimeToLand);
    }

    return toMin(currentTimeRemaining);
  }, [currentTimeRemaining, currentValue, currentTimeToHome, currentTimeToLand]);

  const getState = (): string => {
    if (currentValue <= 0) {
      return theme.palette.crew.batteryGray;
    }

    if (greenDegrees > 0) {
      return theme.palette.crew.batteryGreen;
    }
    if (yellowDegrees > 0) {
      return theme.palette.crew.batteryYellow;
    }

    return theme.palette.crew.batteryRed;
  };

  return (
    <Box className='relative w-full h-full'>
      <Box
        className='aspect-square overflow-hidden rounded-full relative w-full h-full'
        sx={{
          padding: `${padding}px`,
          backgroundColor: 'color-mix(in srgb, var(--joy-palette-common-black) 60%, transparent)',
          border: 1,
          borderColor: 'primary.offWhite'
        }}
      >
        <CrewBatteryLine className='absolute bottom-[5%] left-[22%] rotate-[69deg] z-50 catttt' />
        <CrewBatteryLine className='absolute bottom-[5%] right-[22%] rotate-[6deg] z-50 catttt' />
        {/* Green arc - time_remaining_s (bottom layer) */}
        <Box
          className='absolute top-0 left-0 w-full h-full rounded-full transition-all duration-200 ease-out z-10'
          style={{
            background: `conic-gradient(
              from 210deg,
              ${theme.palette.crew.batteryGreen} 0deg ${greenDegrees}deg,
              transparent ${greenDegrees}deg 360deg
            )`
          }}
        />

        {/* Yellow arc - time_to_home_s (middle layer) */}
        <Box
          className='absolute top-0 left-0 w-full h-full rounded-full transition-all duration-200 ease-out z-20'
          style={{
            background: `conic-gradient(
              from 210deg,
              ${theme.palette.crew.batteryYellow} 0deg ${yellowDegrees}deg,
              transparent ${yellowDegrees}deg 360deg
            )`
          }}
        />

        {/* Red arc - time_to_land_s (top layer) */}
        <Box
          className='absolute top-0 left-0 w-full h-full rounded-full transition-all duration-200 ease-out z-30'
          style={{
            background: `conic-gradient(
              from 210deg,
              ${theme.palette.crew.batteryRed} 0deg ${redDegrees}deg,
              transparent ${redDegrees}deg 360deg
            )`
          }}
        />
        <Box
          className='absolute top-0 left-1/2 -translate-x-1/2 z-10 rounded-b-sm'
          sx={{
            bgcolor: 'primary.offWhite',
            width: `${markerWidth}px`,
            height: `${markerHeight}%`,
            transformOrigin: '50% 400%',
            transform: 'translateX(-50%) rotate(230deg)'
          }}
        />
        <Box
          className='absolute top-0 left-1/2 -translate-x-1/2 z-10 rounded-b-sm'
          sx={{
            bgcolor: 'primary.offWhite',
            width: `${markerWidth}px`,
            height: `${markerHeight}%`,
            transformOrigin: '50% 400%',
            transform: 'translateX(-50%) rotate(130deg)'
          }}
        />

        <Box className='absolute bottom-[0%] sm:bottom-[4%] left-[51%] -translate-x-1/2 z-50 w-full flex justify-center'>
          <Typography
            level='body-xs'
            sx={{
              color: 'primary.offWhite',
              textAlign: 'center'
            }}
          >
            {currentValue > 0 ? `${Math.round(currentValue * 100)}%` : '--'}
          </Typography>
        </Box>

        <Box
          className='z-40 rounded-full w-[70%] h-[70%] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
          sx={{
            backgroundColor: getState(),
            border: 1,
            borderColor: 'primary.offWhite'
          }}
        >
          <Box className='flex flex-col items-center justify-center w-full h-full'>
            {getState() === theme.palette.crew.batteryGray && (
              <>
                <Box className='mt-3' />
                <Typography
                  sx={{
                    color: 'background.body',
                    fontWeight: 400,
                    fontSize: '1.25rem',
                    fontFamily: '"Open Sans", sans-serif',
                    lineHeight: '155%'
                  }}
                >
                  --
                </Typography>
                <Typography
                  level='body-xs'
                  sx={{
                    color: 'background.body'
                  }}
                >
                  {t('crew_view.indicators.battery.minutes')}
                </Typography>
              </>
            )}
            {getState() === theme.palette.crew.batteryGreen && (
              <>
                {!(currentValue > timeToHomeWarn) && (
                  <Typography
                    level='body-xs'
                    sx={{
                      color: 'background.body'
                    }}
                  >
                    {t('crew_view.indicators.battery.rtl')}
                  </Typography>
                )}
                <Typography
                  sx={{
                    color: 'background.body',
                    fontWeight: 400,
                    fontSize: '1.25rem',
                    fontFamily: '"Open Sans", sans-serif',
                    lineHeight: '155%'
                  }}
                >
                  {remainingTimeDisplay}
                </Typography>
                <Typography
                  level='body-xs'
                  sx={{
                    color: 'background.body'
                  }}
                >
                  {t('crew_view.indicators.battery.minutes')}
                </Typography>
              </>
            )}

            {getState() === theme.palette.crew.batteryYellow && (
              <>
                <Typography
                  level='body-xs'
                  sx={{
                    color: 'background.body'
                  }}
                >
                  {t('crew_view.indicators.battery.landing')}
                </Typography>
                <Typography
                  sx={{
                    color: 'background.body',
                    fontWeight: 400,
                    fontSize: '1.25rem',
                    fontFamily: '"Open Sans", sans-serif',
                    lineHeight: '155%'
                  }}
                >
                  {remainingTimeDisplay}
                </Typography>
                <Typography
                  level='body-xs'
                  sx={{
                    color: 'background.body'
                  }}
                >
                  {t('crew_view.indicators.battery.minutes')}
                </Typography>
              </>
            )}
            {getState() === theme.palette.crew.batteryRed && (
              <Typography
                sx={{
                  color: 'background.body',
                  fontWeight: 400,
                  fontSize: '0.8rem',
                  lineHeight: '155%',
                  fontFamily: '"Open Sans", sans-serif'
                }}
              >
                {t('crew_view.indicators.battery.landing')}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Battery;
